import Phaser from 'phaser';
import { COLORS, BossPhase, BossAIRecord } from '../utils/constants';
import { soundManager } from '../systems/SoundManager';

interface AttackPattern {
  type: 'melee' | 'ranged' | 'slam' | 'charge' | 'projectile_burst';
  damage: number;
  cooldown: number;
  range: number;
  speed: number;
}

const PHASE_1_PATTERNS: AttackPattern[] = [
  { type: 'melee', damage: 8, cooldown: 1500, range: 60, speed: 0 },
  { type: 'ranged', damage: 5, cooldown: 2500, range: 400, speed: 300 },
];

const PHASE_2_PATTERNS: AttackPattern[] = [
  { type: 'melee', damage: 10, cooldown: 1200, range: 70, speed: 0 },
  { type: 'ranged', damage: 7, cooldown: 2000, range: 450, speed: 350 },
  { type: 'slam', damage: 15, cooldown: 3000, range: 100, speed: 0 },
  { type: 'charge', damage: 12, cooldown: 3500, range: 500, speed: 500 },
];

const PHASE_3_PATTERNS: AttackPattern[] = [
  { type: 'melee', damage: 12, cooldown: 900, range: 75, speed: 0 },
  { type: 'ranged', damage: 10, cooldown: 1500, range: 500, speed: 400 },
  { type: 'slam', damage: 18, cooldown: 2500, range: 120, speed: 0 },
  { type: 'charge', damage: 15, cooldown: 3000, range: 600, speed: 600 },
  { type: 'projectile_burst', damage: 6, cooldown: 4000, range: 500, speed: 350 },
];

export class Boss {
  scene: Phaser.Scene;
  sprite: Phaser.Physics.Arcade.Sprite;
  hp = 500;
  maxHp = 500;
  phase: BossPhase = 1;
  isAlive = true;

  private patterns: AttackPattern[];
  private lastAttackTime = 0;
  private currentCooldown = 2000;
  private targetX = 0;
  private aiRecord: BossAIRecord;
  private behaviorProfile: 'aggressive' | 'defensive' | 'balanced' = 'balanced';
  private attackBias = { left: 0, right: 0, ranged: 0, melee: 0 };
  private chargeTarget: { x: number; y: number } | null = null;
  private charging = false;
  private chargeSpeed = 0;
  private graphics: Phaser.GameObjects.Graphics;
  private eyeGlow: Phaser.GameObjects.Graphics;
  private slamWarning: Phaser.GameObjects.Graphics | null = null;
  private projectiles: Phaser.GameObjects.Arc[] = [];
  private difficultyMultiplier = 1;
  private retreatTimer = 0;
  private hitInvulnerable = false;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.scene = scene;
    this.patterns = PHASE_1_PATTERNS;
    this.aiRecord = {
      playerMovementDirection: 'center',
      attackFrequency: 0,
      skillUsage: 0,
      dodgeCount: 0,
      damageTaken: 0,
      playerDamageDealt: 0,
      leftDodges: 0,
      rightDodges: 0,
      aggressiveMoves: 0,
      defensiveMoves: 0,
    };

    this.createTexture();

    this.sprite = scene.physics.add.sprite(x, y, 'boss');
    this.sprite.setCollideWorldBounds(true);
    (this.sprite.body as Phaser.Physics.Arcade.Body).setSize(60, 80);
    (this.sprite.body as Phaser.Physics.Arcade.Body).setOffset(15, 15);
    this.sprite.setGravityY(0);
    this.sprite.setDepth(8);

    this.graphics = scene.add.graphics().setDepth(11);
    this.eyeGlow = scene.add.graphics().setDepth(12);
  }

  private createTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = 90;
    canvas.height = 100;
    const ctx = canvas.getContext('2d')!;
    const cx = 45;

    // Shadow aura
    ctx.shadowColor = '#9945ff';
    ctx.shadowBlur = 20;

    // Main body - heavy mech
    ctx.shadowBlur = 10;
    ctx.fillStyle = '#2a1a3a';
    ctx.fillRect(15, 30, 60, 50);

    // Chest armor plate
    ctx.fillStyle = '#9945ff';
    ctx.fillRect(18, 34, 54, 8);
    ctx.fillStyle = '#4a2a6a';
    ctx.fillRect(20, 44, 50, 24);

    // Core reactor
    ctx.shadowBlur = 16;
    ctx.fillStyle = '#ff2d7b';
    ctx.beginPath();
    ctx.arc(cx, 52, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(cx, 52, 4, 0, Math.PI * 2);
    ctx.fill();

    // Head - menacing helmet
    ctx.shadowBlur = 8;
    ctx.fillStyle = '#3a2a4a';
    ctx.fillRect(20, 2, 50, 30);
    // Helmet crest
    ctx.fillStyle = '#ff2d7b';
    ctx.fillRect(40, 0, 10, 8);

    // Eyes - glowing red
    ctx.shadowBlur = 12;
    ctx.fillStyle = '#ff1744';
    ctx.beginPath();
    ctx.arc(32, 14, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(58, 14, 6, 0, Math.PI * 2);
    ctx.fill();
    // Eye pupils
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(32, 14, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(58, 14, 2, 0, Math.PI * 2);
    ctx.fill();

    // Jaw / mouth grill
    ctx.shadowBlur = 4;
    ctx.fillStyle = '#1a1a2a';
    ctx.fillRect(26, 20, 38, 8);
    ctx.fillStyle = '#555577';
    ctx.fillRect(28, 22, 4, 4);
    ctx.fillRect(34, 22, 4, 4);
    ctx.fillRect(40, 22, 4, 4);
    ctx.fillRect(46, 22, 4, 4);
    ctx.fillRect(52, 22, 4, 4);

    // Shoulder cannons
    ctx.shadowBlur = 8;
    ctx.fillStyle = '#4a2a6a';
    ctx.fillRect(2, 28, 14, 16);
    ctx.fillRect(74, 28, 14, 16);
    ctx.fillStyle = '#ff2d7b';
    ctx.fillRect(4, 32, 10, 4);
    ctx.fillRect(76, 32, 10, 4);

    // Arms
    ctx.fillStyle = '#3a2a4a';
    ctx.fillRect(6, 46, 10, 24);
    ctx.fillRect(74, 46, 10, 24);
    // Arm cannons
    ctx.fillStyle = '#555577';
    ctx.fillRect(4, 60, 14, 8);
    ctx.fillRect(72, 60, 14, 8);
    ctx.fillStyle = '#ff2d7b';
    ctx.fillRect(6, 62, 10, 2);
    ctx.fillRect(74, 62, 10, 2);

    // Legs - heavy stompers
    ctx.shadowBlur = 6;
    ctx.fillStyle = '#2a1a3a';
    ctx.fillRect(18, 80, 20, 18);
    ctx.fillRect(52, 80, 20, 18);
    // Knee joints
    ctx.fillStyle = '#555577';
    ctx.fillRect(20, 84, 4, 4);
    ctx.fillRect(54, 84, 4, 4);
    // Feet
    ctx.fillStyle = '#4a2a6a';
    ctx.fillRect(14, 92, 26, 6);
    ctx.fillRect(50, 92, 26, 6);
    ctx.fillStyle = '#ff2d7b';
    ctx.fillRect(16, 94, 22, 2);
    ctx.fillRect(52, 94, 22, 2);

    // Phase color trim
    ctx.shadowBlur = 0;

    // Back thrusters
    ctx.fillStyle = '#222244';
    ctx.fillRect(10, 50, 6, 20);
    ctx.fillRect(74, 50, 6, 20);
    ctx.fillStyle = '#9945ff';
    ctx.fillRect(12, 66, 2, 6);
    ctx.fillRect(76, 66, 2, 6);

    if (!this.scene.textures.exists('boss')) {
      const tex = this.scene.textures.createCanvas('boss', 90, 100);
      tex.context.drawImage(canvas, 0, 0);
      tex.refresh();
    }
  }

  setDifficulty(multiplier: number) {
    this.difficultyMultiplier = multiplier;
    this.currentCooldown = 2000 / multiplier;
  }

  update(time: number, _delta: number, playerX: number, playerY: number) {
    if (!this.isAlive) return;

    this.updatePhase();
    this.handleMovement(playerX, playerY, time);
    this.handleAttack(time, playerX, playerY);
    this.updateProjectiles(time);
    this.drawEffects(time);
  }

  private updatePhase() {
    const hpPercent = this.hp / this.maxHp;
    if (hpPercent <= 0.33 && this.phase !== 3) {
      this.phase = 3;
      this.patterns = PHASE_3_PATTERNS;
    } else if (hpPercent <= 0.66 && this.phase === 1) {
      this.phase = 2;
      this.patterns = PHASE_2_PATTERNS;
    }
  }

  private handleMovement(playerX: number, playerY: number, time: number) {
    if (this.charging) {
      this.handleCharge();
      return;
    }

    const body = this.sprite.body as Phaser.Physics.Arcade.Body;
    const dx = playerX - this.sprite.x;
    const dist = Math.abs(dx);

    // Maintain preferred distance based on behavior profile
    let preferredDist = 150;
    if (this.behaviorProfile === 'aggressive') preferredDist = 80;
    if (this.behaviorProfile === 'defensive') preferredDist = 250;

    // Phase 3: faster movement
    const moveSpeed = this.phase === 3 ? 120 : this.phase === 2 ? 90 : 60;

    if (dist > preferredDist + 30) {
      body.setVelocityX(dx > 0 ? moveSpeed : -moveSpeed);
      this.sprite.setFlipX(dx < 0);
    } else if (dist < preferredDist - 30) {
      body.setVelocityX(dx > 0 ? -moveSpeed * 0.5 : moveSpeed * 0.5);
      this.sprite.setFlipX(dx > 0);
    } else {
      body.setVelocityX(0);
    }

    // Track player side bias
    if (playerX < this.scene.cameras.main.width / 2) {
      this.attackBias.left++;
    } else {
      this.attackBias.right++;
    }
  }

  private handleAttack(time: number, playerX: number, playerY: number) {
    if (this.charging) return;
    if (time - this.lastAttackTime < this.currentCooldown) return;

    const dx = playerX - this.sprite.x;
    const dy = playerY - this.sprite.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Choose attack based on distance and AI adaptation
    const available = this.patterns.filter((p) => dist <= p.range * 1.5);
    if (available.length === 0) return;

    // Weight selection based on AI learning
    const weighted = this.weightedSelection(available);
    if (!weighted) return;

    this.lastAttackTime = time;

    switch (weighted.type) {
      case 'melee':
        this.performMelee(playerX, playerY, weighted);
        break;
      case 'ranged':
        this.performRanged(playerX, playerY, weighted);
        break;
      case 'slam':
        this.performSlam(playerX, playerY, weighted);
        break;
      case 'charge':
        this.performCharge(playerX, playerY, weighted);
        break;
      case 'projectile_burst':
        this.performProjectileBurst(playerX, playerY, weighted);
        break;
    }
  }

  private weightedSelection(patterns: AttackPattern[]): AttackPattern | null {
    if (patterns.length === 0) return null;

    const weights = patterns.map((p) => {
      let w = 1;
      if (p.type === 'ranged') w += this.attackBias.ranged * 0.01;
      if (p.type === 'melee') w += this.attackBias.melee * 0.01;
      if (this.behaviorProfile === 'aggressive' && (p.type === 'melee' || p.type === 'charge')) w *= 1.5;
      if (this.behaviorProfile === 'defensive' && (p.type === 'ranged' || p.type === 'slam')) w *= 1.5;
      return Math.max(0.1, w);
    });

    const total = weights.reduce((a, b) => a + b, 0);
    let r = Math.random() * total;
    for (let i = 0; i < patterns.length; i++) {
      r -= weights[i];
      if (r <= 0) return patterns[i];
    }
    return patterns[0];
  }

  private performMelee(px: number, py: number, pattern: AttackPattern) {
    this.attackBias.melee++;

    // Melee attack flash
    const flash = this.scene.add.graphics().setDepth(15);
    const dir = this.sprite.x < px ? 1 : -1;
    flash.fillStyle(COLORS.red, 0.4);
    flash.fillCircle(this.sprite.x + dir * 40, this.sprite.y, 35);
    this.scene.time.delayedCall(150, () => flash.destroy());

    // Check hit via event
    const dist = Math.sqrt((px - this.sprite.x) ** 2 + (py - this.sprite.y) ** 2);
    if (dist <= pattern.range) {
      this.scene.events.emit('boss-attack-hit', pattern.damage, 'melee');
    }
  }

  private performRanged(px: number, py: number, pattern: AttackPattern) {
    this.attackBias.ranged++;

    const angle = Math.atan2(py - this.sprite.y, px - this.sprite.x);
    const proj = this.scene.add.circle(this.sprite.x, this.sprite.y, 6, COLORS.red, 1).setDepth(13);
    this.scene.physics.add.existing(proj);
    (proj.body as Phaser.Physics.Arcade.Body).setCircle(6);
    (proj.body as Phaser.Physics.Arcade.Body).setBounce(0);
    (proj.body as Phaser.Physics.Arcade.Body).setCollideWorldBounds(false);

    this.projectiles.push(proj);

    const vx = Math.cos(angle) * pattern.speed * this.difficultyMultiplier;
    const vy = Math.sin(angle) * pattern.speed * this.difficultyMultiplier;
    (proj.body as Phaser.Physics.Arcade.Body).setVelocity(vx, vy);

    // Store metadata
    (proj as any)._damage = pattern.damage;
    (proj as any)._createdAt = this.scene.time.now;
    (proj as any)._range = pattern.range;
    (proj as any)._startX = this.sprite.x;
    (proj as any)._startY = this.sprite.y;
  }

  private performSlam(_px: number, _py: number, pattern: AttackPattern) {
    // Warning indicator
    this.slamWarning = this.scene.add.graphics().setDepth(14);
    this.slamWarning.fillStyle(COLORS.red, 0.15);
    this.slamWarning.fillRect(this.sprite.x - 80, this.scene.cameras.main.height - 60, 160, 60);
    this.slamWarning.lineStyle(2, COLORS.red, 0.5);
    this.slamWarning.strokeRect(this.sprite.x - 80, this.scene.cameras.main.height - 60, 160, 60);

    this.scene.time.delayedCall(600, () => {
      if (this.slamWarning) {
        this.slamWarning.destroy();
        this.slamWarning = null;
      }

      // Slam shockwave
      const shockwave = this.scene.add.graphics().setDepth(14);
      shockwave.fillStyle(COLORS.red, 0.3);
      shockwave.fillRect(this.sprite.x - 100, this.scene.cameras.main.height - 50, 200, 40);
      this.scene.time.delayedCall(200, () => shockwave.destroy());

      this.scene.events.emit('boss-slam', pattern.damage);
    });
  }

  private performCharge(px: number, py: number, pattern: AttackPattern) {
    this.charging = true;
    this.chargeTarget = { x: px, y: py };
    this.chargeSpeed = pattern.speed * this.difficultyMultiplier;

    // Wind-up flash
    const flash = this.scene.add.graphics().setDepth(14);
    flash.fillStyle(COLORS.yellow, 0.3);
    flash.fillCircle(this.sprite.x, this.sprite.y, 40);
    this.scene.time.delayedCall(300, () => flash.destroy());
  }

  private handleCharge() {
    if (!this.chargeTarget || !this.charging) return;

    const dx = this.chargeTarget.x - this.sprite.x;
    const dy = this.chargeTarget.y - this.sprite.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < 20) {
      this.charging = false;
      this.chargeTarget = null;
      (this.sprite.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
      return;
    }

    const vx = (dx / dist) * this.chargeSpeed;
    const vy = (dy / dist) * this.chargeSpeed;
    (this.sprite.body as Phaser.Physics.Arcade.Body).setVelocity(vx, vy);

    this.sprite.setFlipX(dx < 0);

    this.scene.events.emit('boss-charge-check', this.sprite.x, this.sprite.y);
  }

  private performProjectileBurst(px: number, py: number, pattern: AttackPattern) {
    const count = this.phase === 3 ? 5 : 3;
    for (let i = 0; i < count; i++) {
      const angle = Math.atan2(py - this.sprite.y, px - this.sprite.x);
      const spread = ((i - (count - 1) / 2) * 0.3);
      const proj = this.scene.add.circle(this.sprite.x, this.sprite.y, 5, COLORS.pink, 1).setDepth(13);
      this.scene.physics.add.existing(proj);
      (proj.body as Phaser.Physics.Arcade.Body).setCircle(5);

      this.projectiles.push(proj);

      const vx = Math.cos(angle + spread) * pattern.speed * this.difficultyMultiplier;
      const vy = Math.sin(angle + spread) * pattern.speed * this.difficultyMultiplier;
      (proj.body as Phaser.Physics.Arcade.Body).setVelocity(vx, vy);

      (proj as any)._damage = pattern.damage;
      (proj as any)._createdAt = this.scene.time.now;
      (proj as any)._range = pattern.range;
      (proj as any)._startX = this.sprite.x;
      (proj as any)._startY = this.sprite.y;
    }
  }

  private updateProjectiles(time: number) {
    const cam = this.scene.cameras.main;
    this.projectiles = this.projectiles.filter((p) => {
      if (!p.active) return false;
      const elapsed = time - (p as any)._createdAt;
      const startDist = Math.sqrt(
        (p.x - (p as any)._startX) ** 2 + (p.y - (p as any)._startY) ** 2
      );
      if (elapsed > 3000 || startDist > (p as any)._range + 50 ||
        p.x < -50 || p.x > cam.width + 50 || p.y < -50 || p.y > cam.height + 50) {
        p.destroy();
        return false;
      }
      return true;
    });
  }

  takeDamage(amount: number, type: 'normal' | 'heavy' | 'skill' = 'normal') {
    if (this.hitInvulnerable) return;
    this.hitInvulnerable = true;
    this.scene.time.delayedCall(250, () => { this.hitInvulnerable = false; });

    let finalDamage = amount;
    if (type === 'heavy') finalDamage = amount * 1.5;
    if (type === 'skill') finalDamage = amount * 2;

    this.hp = Math.max(0, this.hp - finalDamage);
    this.aiRecord.playerDamageDealt += finalDamage;
    soundManager.play('boss_hit');

    // Flash
    this.sprite.setTint(0xffffff);
    this.scene.time.delayedCall(80, () => {
      if (this.sprite.active) this.sprite.clearTint();
    });

    if (this.hp <= 0) {
      this.die();
    }
  }

  private die() {
    this.isAlive = false;
    (this.sprite.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
    soundManager.play('boss_death');

    // Death effect
    const flash = this.scene.add.graphics().setDepth(20);
    flash.fillStyle(COLORS.purple, 0.6);
    flash.fillCircle(this.sprite.x, this.sprite.y, 60);
    this.scene.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 800,
      onComplete: () => flash.destroy(),
    });

    this.sprite.setAlpha(0);
    this.scene.events.emit('boss-defeated');
  }

  learnFromPlayer(record: Partial<BossAIRecord>) {
    Object.assign(this.aiRecord, record);

    const total = this.aiRecord.leftDodges + this.aiRecord.rightDodges;
    if (total > 5) {
      if (this.aiRecord.leftDodges > this.aiRecord.rightDodges * 1.5) {
        this.attackBias.left += 20;
      } else if (this.aiRecord.rightDodges > this.aiRecord.leftDodges * 1.5) {
        this.attackBias.right += 20;
      }
    }

    if (this.aiRecord.aggressiveMoves > this.aiRecord.defensiveMoves * 1.5) {
      this.behaviorProfile = 'defensive';
    } else if (this.aiRecord.defensiveMoves > this.aiRecord.aggressiveMoves * 1.5) {
      this.behaviorProfile = 'aggressive';
    } else {
      this.behaviorProfile = 'balanced';
    }

    if (this.aiRecord.skillUsage > 3) {
      this.behaviorProfile = 'defensive';
      this.currentCooldown *= 0.9;
    }

    this.currentCooldown = Math.max(600, this.currentCooldown * 0.95);
  }

  private drawEffects(time: number) {
    this.graphics.clear();
    this.eyeGlow.clear();

    if (!this.isAlive) return;

    // Eye glow pulse
    const pulse = 0.4 + Math.sin(time * 0.005) * 0.3;
    this.eyeGlow.fillStyle(COLORS.red, pulse);
    this.eyeGlow.fillCircle(this.sprite.x - 10, this.sprite.y - 28, 5);
    this.eyeGlow.fillCircle(this.sprite.x + 10, this.sprite.y - 28, 5);

    // Phase indicator aura
    const auraColor = this.phase === 3 ? COLORS.red : this.phase === 2 ? COLORS.yellow : COLORS.purple;
    this.graphics.lineStyle(1, auraColor, 0.3 + Math.sin(time * 0.003) * 0.15);
    this.graphics.strokeCircle(this.sprite.x, this.sprite.y, 45);

    // Charge warning
    if (this.charging) {
      this.graphics.lineStyle(3, COLORS.yellow, 0.6);
      this.graphics.lineBetween(this.sprite.x, this.sprite.y, this.chargeTarget!.x, this.chargeTarget!.y);
    }
  }

  getProjectiles() {
    return this.projectiles;
  }

  isCharging(): boolean {
    return this.charging;
  }

  destroy() {
    this.sprite.destroy();
    this.graphics.destroy();
    this.eyeGlow.destroy();
    this.projectiles.forEach((p) => p.destroy());
    if (this.slamWarning) this.slamWarning.destroy();
  }
}
